import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const UPDATED = '2026-10-21';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Looking for a vegan, allergen-free turmeric supplement?</strong>
      <span className="block text-emerald-800/80 mt-0.5">IIY Phytoventra BCM-95® — vegan capsules, allergen-free, solar-powered GMP. £44.99 (first bottle £37.99), UK VAT &amp; delivery included.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors" data-testid="hub-inline-cta">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const GuideLink = ({ to, children }) => (
  <Link to={to} className="text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-2 hover:decoration-emerald-600">{children}</Link>
);

const sections = [
  {
    id: 'quick-answer',
    heading: 'Vegan, allergen-free turmeric supplements in the UK',
    body: (
      <>
        <p>Many turmeric capsules use animal-derived gelatin shells, and some carry allergens or unnecessary excipients. If you are vegan — or simply want a clean label — the thing to check is both the <strong>capsule shell</strong> and the <strong>full ingredient and allergen declaration</strong>, not just the turmeric extract.</p>
        <p><strong>IIY Phytoventra BCM-95<sup>&reg;</sup></strong> is <strong>100% vegan</strong>: the capsules are vegetable cellulose, not gelatin. It is <strong>allergen-free</strong> (no gluten, soya, dairy or nuts), contains no artificial colours, and is manufactured under <strong>GMP at a facility powered entirely by solar energy</strong>.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'what-makes-vegan',
    heading: 'What actually makes a turmeric supplement vegan',
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>The capsule shell</strong> — many are made from gelatin (animal-derived). Vegan products use plant cellulose (HPMC) capsules.</li>
          <li><strong>Flow agents &amp; carriers</strong> — some formulas add magnesium stearate or other excipients; a clean label keeps these to a minimum.</li>
          <li><strong>Absorption enhancers</strong> — phospholipid formulas can use soy lecithin; BCM-95<sup>&reg;</sup> uses turmeric&rsquo;s own oils, so there is no soy-based carrier.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'allergen-free',
    heading: 'Allergen-free and clean-label — what to check',
    body: (
      <>
        <p>&ldquo;Allergen-free&rdquo; should be backed by a clear declaration against the 14 major allergens. On the IIY Phytoventra BCM-95<sup>&reg;</sup> label you will find:</p>
        <ul className="not-prose space-y-2.5 my-2">
          {[
            'Vegan vegetable-cellulose capsules — no gelatin.',
            'No gluten, soya, dairy or nuts; no artificial colours.',
            '650 mg curcumin per 2-capsule serving, from the patented BCM-95® / Curcugreen® extract — piperine-free.',
            'Manufactured under GMP using 100% solar power.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-slate-700">
              <Check className="w-4 h-4 mt-1 shrink-0 text-emerald-600" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p>Compare formulations in <GuideLink to="/best-curcumin-supplement-uk">Best Curcumin Supplement UK</GuideLink>, or start with <GuideLink to="/turmeric-supplements-uk">Turmeric Supplements UK</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Vegan turmeric supplement — frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">Are turmeric supplements vegan?</p>
        <p>Not all of them — it depends on the capsule. Gelatin capsules are animal-derived, while vegan products use plant-cellulose capsules. IIY Phytoventra BCM-95<sup>&reg;</sup> uses 100% vegan vegetable-cellulose capsules.</p>
        <p className="font-semibold text-emerald-900 mt-4">Is BCM-95® allergen-free?</p>
        <p>Yes. BCM-95<sup>&reg;</sup> is free from gluten, soya, dairy and nuts, with no artificial colours, and the capsules themselves are allergen-free and vegan.</p>
        <p className="font-semibold text-emerald-900 mt-4">Where is it made?</p>
        <p>The BCM-95<sup>&reg;</sup> extract is produced under GMP at a facility powered entirely by solar energy, and the finished product is sold in the UK directly by IIY Phytoventra at iiypv.com.</p>
      </>
    ),
  },
];

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { q: 'Are turmeric supplements vegan?', a: 'Not all of them — it depends on the capsule. Gelatin capsules are animal-derived, while vegan products use plant-cellulose capsules. IIY Phytoventra BCM-95® uses 100% vegan vegetable-cellulose capsules.' },
    { q: 'Is BCM-95® allergen-free?', a: 'Yes. BCM-95® is free from gluten, soya, dairy and nuts, with no artificial colours, and the capsules themselves are allergen-free and vegan.' },
    { q: 'Where is BCM-95® made?', a: 'The BCM-95® extract is produced under GMP at a facility powered entirely by solar energy, and the finished product is sold in the UK directly by IIY Phytoventra at iiypv.com.' },
  ].map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function VeganTurmericSupplementUK() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="Vegan & Allergen-Free Turmeric Supplement UK | BCM-95®"
        description="How to find a genuinely vegan, allergen-free turmeric supplement in the UK — vegan capsules vs gelatin, clean-label checks, and the solar-powered GMP BCM-95® option."
        keywords="vegan turmeric supplement UK, allergen-free turmeric, vegan curcumin supplement, gelatin-free turmeric capsules, clean label turmeric, BCM-95, Curcugreen, soya-free curcumin"
        pathOverride="/vegan-turmeric-supplement-uk"
        heading="Vegan & Allergen-Free Turmeric Supplement UK"
        subheading="Not every turmeric capsule is vegan. Here's what to check — from the capsule shell to allergens and carriers — and the clean-label, solar-made UK option."
        tag="Vegan Turmeric UK"
        datePublished={UPDATED}
        dateModified={UPDATED}
        sections={sections}
      />
    </>
  );
}
